import { XMarkIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useState } from 'react';

import { Badge } from '@/components/core/Badge';
import { Combobox } from '@/components/core/Combobox';
import { DateInput } from '@/components/core/DateInput';
import { FormattedDate } from '@/components/core/FormattedDate';
import { HTMLInput } from '@/components/core/HTMLInput';
import { IconButton } from '@/components/core/IconButton';
import { Input } from '@/components/core/Input';
import { useTaskUsers } from '@/dialogs/create-task/createTask.queries';
import { FormResetProvider } from '@/dialogs/item-editor/Context/FormResetContext';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { logger } from '@/services/logger';

import {
    canEditTask,
    definedTaskTags,
    type EditorTask,
    isTaskFinished,
    taskStatusBadgeVariant,
    userDisplayName,
} from './taskView';
import { saveTask } from './taskView.queries';

type Option = { value: string; label: string };

type TaskFormApi = {
    save: () => Promise<boolean>;
    /** Edits to merge into another save, such as Close Task. `null` when validation fails. */
    draft: () => Partial<EditorTask> | null;
    saving: boolean;
    editable: boolean;
    dirty: boolean;
};

type TaskInteractiveDetailsProps = {
    task: EditorTask;
    onTaskUpdated?: (task: EditorTask) => void;
    onFormBind?: (api: TaskFormApi) => void;
};

const toDate = (value?: EditorTask['dueDate']): Date | undefined => {
    if (value == null || value === '') {
        return undefined;
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

/** DateInput portals into `#root-portal`. The tasks page does not have that node, and the picker throws without it. */
const ensureDatePortal = () => {
    if (typeof document === 'undefined' || document.getElementById('root-portal')) {
        return;
    }
    const portal = document.createElement('div');
    portal.id = 'root-portal';
    document.body.appendChild(portal);
};

const sameList = (left: string[], right: string[]) => (
    left.length === right.length && left.every((item, index) => item === right[index])
);

const isBlankHtml = (value: string) => value.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim() === '';

const FormField = ({
    label,
    htmlFor,
    clearId,
    canClear,
    onClear,
    children,
}: {
    label: string;
    htmlFor?: string;
    clearId?: string;
    canClear?: boolean;
    onClear?: () => void;
    children: ReactNode;
}) => {
    const { t } = useTranslation();

    return (
        <>
            <label
                htmlFor={htmlFor}
                className="tw-text-t-xs tw-text-t-secondary lg:tw-pt-2 lg:tw-text-t-sm"
            >
                {label}
            </label>
            <div className="tw-flex tw-min-w-0 tw-items-start tw-gap-2">
                <div className="tw-min-w-0 tw-flex-1">{children}</div>
                {onClear ? (
                    <IconButton
                        id={clearId}
                        className="tw-mt-1.5 tw-h-6 tw-w-6 tw-shrink-0"
                        type="button"
                        size="md"
                        variant="destructive"
                        icon={XMarkIcon}
                        aria-label={`${t('CLEAR')} ${label}`}
                        disabled={!canClear}
                        onClick={onClear}
                    />
                ) : (
                    <span className="tw-mt-1.5 tw-h-6 tw-w-6 tw-shrink-0" aria-hidden="true" />
                )}
            </div>
        </>
    );
};

const TaskInteractiveDetails = ({ task, onTaskUpdated, onFormBind }: TaskInteractiveDetailsProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const canEditFields = canEditTask(task) && !isTaskFinished(task);
    const canEditAssignee = !isTaskFinished(task);
    const tagChoices = definedTaskTags();
    const editable = canEditFields || canEditAssignee;

    const [title, setTitle] = useState(task.title ?? '');
    const [dueDate, setDueDate] = useState<Date | undefined>(toDate(task.dueDate));
    const [assigneeId, setAssigneeId] = useState(task.assignee ?? '');
    const [notes, setNotes] = useState(task.notes ?? '');
    const [tags, setTags] = useState<string[]>(task.tags ?? []);
    const [saving, setSaving] = useState(false);
    const { data: users = [] } = useTaskUsers({ enabled: canEditAssignee });
    const savedDueDate = toDate(task.dueDate);
    const dirty = (canEditFields && (
        title !== (task.title ?? '')
        || (dueDate?.getTime() ?? null) !== (savedDueDate?.getTime() ?? null)
        || notes !== (task.notes ?? '')
        || (!!tagChoices && !sameList(tags, task.tags ?? []))
    )) || (canEditAssignee && assigneeId !== (task.assignee ?? ''));

    useEffect(() => {
        setTitle(task.title ?? '');
        setDueDate(toDate(task.dueDate));
        setAssigneeId(task.assignee ?? '');
        setNotes(task.notes ?? '');
        setTags(task.tags ?? []);
    }, [task]);

    const draft = useCallback((): Partial<EditorTask> | null => {
        if (canEditFields && !title.trim()) {
            toast.warning(t('TASK.MISSING_TASK_TITLE'));
            return null;
        }
        const patch: Partial<EditorTask> = {};
        if (canEditFields) {
            patch.title = title.trim();
            patch.dueDate = dueDate ? dueDate.toISOString() : null;
            patch.notes = notes;
            if (tagChoices) {
                patch.tags = tags;
            }
        }
        if (canEditAssignee) {
            patch.assignee = assigneeId;
        }
        return patch;
    }, [assigneeId, canEditAssignee, canEditFields, dueDate, notes, t, tagChoices, tags, title, toast]);

    const save = useCallback(async (): Promise<boolean> => {
        if (!dirty) {
            return false;
        }
        const patch = draft();
        if (!patch) {
            return false;
        }

        setSaving(true);
        try {
            const saved = await saveTask({ ...task, ...patch });
            queryClient.setQueryData(['task', { taskId: task.id }], saved);
            await queryClient.invalidateQueries({ queryKey: ['taskComments'] });
            onTaskUpdated?.(saved);
            toast.success(t('TASK.SAVE_TASK_SUCCESS_MESSAGE'));
            return true;
        } catch (error) {
            logger.error('Saving task failed', error);
            toast.error(t('ERROR_OCCURRED'));
            return false;
        } finally {
            setSaving(false);
        }
    }, [dirty, draft, onTaskUpdated, queryClient, t, task, toast]);

    useEffect(() => {
        onFormBind?.({ save, draft, saving, editable, dirty });
    }, [dirty, draft, editable, onFormBind, save, saving]);

    useLayoutEffect(() => {
        ensureDatePortal();
    }, []);

    const userOptions: Option[] = users.map((user) => ({
        value: user.userId,
        label: user.displayName ?? user.userId,
    }));
    const assigneeOption = userOptions.find((option) => option.value === assigneeId)
        ?? (assigneeId ? { value: assigneeId, label: userDisplayName(assigneeId) || assigneeId } : null);
    const tagOptions: Option[] = (tagChoices ?? []).map((tag) => ({ value: tag, label: tag }));

    return (
        <FormResetProvider>
            <div className="tw-grid tw-grid-cols-1 tw-gap-4 lg:tw-grid-cols-[11rem_minmax(0,1fr)] lg:tw-gap-x-4 lg:tw-gap-y-4">
                <FormField
                    label={t('TASK.TITLE')}
                    htmlFor="task-title"
                    clearId="clear-task-title"
                    canClear={canEditFields && !saving && title !== ''}
                    onClear={() => setTitle('')}
                >
                    <Input
                        id="task-title"
                        value={title}
                        disabled={!canEditFields || saving}
                        onChange={(event) => setTitle(event.target.value)}
                    />
                </FormField>
                <FormField
                    label={t('TASK.DUE_DATE')}
                    htmlFor="task-due-date"
                    clearId="clear-task-due-date"
                    canClear={canEditFields && !saving && dueDate != null}
                    onClear={() => setDueDate(undefined)}
                >
                    <DateInput
                        id="task-due-date"
                        selectedDate={dueDate}
                        disabled={!canEditFields || saving}
                        onChange={(value) => setDueDate(value ?? undefined)}
                    />
                </FormField>
                <FormField label={t('CREATED_AT')}>
                    <span className="tw-block tw-text-t-sm tw-text-t-primary lg:tw-pt-2 lg:tw-text-t-base">
                        <FormattedDate value={task.createDate} format="fullDate" />
                        {' '}
                        {t('TIME.AT')}
                        {' '}
                        <FormattedDate value={task.createDate} format="mediumTime" />
                    </span>
                </FormField>
                <FormField label={t('TASK.AUTHOR')}>
                    <span className="tw-block tw-text-t-sm tw-text-t-primary lg:tw-pt-2 lg:tw-text-t-base">
                        {userDisplayName(task.author) || '\u00a0'}
                    </span>
                </FormField>
                <FormField
                    label={t('TASK.ASSIGNEE')}
                    htmlFor="task-assignee"
                    clearId="clear-task-assignee"
                    canClear={canEditAssignee && !saving && assigneeId !== ''}
                    onClear={() => setAssigneeId('')}
                >
                    <Combobox
                        inputId="task-assignee"
                        isDisabled={!canEditAssignee || saving}
                        options={userOptions}
                        value={assigneeOption}
                        placeholder=""
                        onChange={(option: Option | null) => setAssigneeId(option?.value ?? '')}
                    />
                </FormField>
                <FormField
                    label={t('TASK.NOTES')}
                    htmlFor="task-notes"
                    clearId="clear-task-notes"
                    canClear={canEditFields && !saving && !isBlankHtml(notes)}
                    onClear={() => setNotes('')}
                >
                    <HTMLInput
                        id="task-notes"
                        rows={4}
                        readOnly={!canEditFields || saving}
                        value={notes}
                        onChange={setNotes}
                    />
                </FormField>
                {tagChoices && (
                    <FormField
                        label={t('TASK.TAGS')}
                        htmlFor="task-tags"
                        clearId="clear-task-tags"
                        canClear={canEditFields && !saving && tags.length > 0}
                        onClear={() => setTags([])}
                    >
                        <Combobox
                            inputId="task-tags"
                            isMulti
                            isDisabled={!canEditFields || saving}
                            options={tagOptions}
                            value={tags.map((tag) => ({ value: tag, label: tag }))}
                            placeholder=""
                            onChange={(options: readonly Option[] | null) => {
                                setTags((options ?? []).map((option) => option.value));
                            }}
                        />
                    </FormField>
                )}
                <FormField label={t('TASK.CHANGE_STATUS')}>
                    <span className="tw-inline-flex lg:tw-pt-1">
                        <Badge variant={taskStatusBadgeVariant(task.taskStatus)}>
                            {task.taskStatus}
                        </Badge>
                    </span>
                </FormField>
            </div>
        </FormResetProvider>
    );
};

export type { TaskFormApi };
export { TaskInteractiveDetails };
